import RefreshIcon from "@mui/icons-material/Refresh";
import {
	Autocomplete,
	Box,
	IconButton,
	TextField,
	Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import type { ProductSizeOption } from "../models/ProductChanges";

interface Props {
	start: Moment;
	end: Moment;
	onStartChange: (date: Moment | null) => void;
	onEndChange: (date: Moment | null) => void;
	productSizeId: number | null;
	onProductSizeChange: (id: number | null) => void;
	productSizes: ProductSizeOption[];
	onRefresh: () => void;
	onExportExcel: () => void;
	isLoading: boolean;
}

export const ProductChangesToolbar = ({
	start,
	end,
	onStartChange,
	onEndChange,
	productSizeId,
	onProductSizeChange,
	productSizes,
	onRefresh,
	onExportExcel,
	isLoading,
}: Props) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
			px: 1.5,
			py: 0.75,
			flexWrap: "wrap",
		}}
	>
		<Box sx={{ minWidth: 150, maxWidth: 170 }}>
			<DatePicker
				label="Start"
				format="MM/DD/YYYY"
				value={start}
				maxDate={end}
				onChange={onStartChange}
				slotProps={{ textField: { size: "small", fullWidth: true } }}
			/>
		</Box>
		<Box sx={{ minWidth: 150, maxWidth: 170 }}>
			<DatePicker
				label="End"
				format="MM/DD/YYYY"
				value={end}
				minDate={start}
				onChange={onEndChange}
				slotProps={{ textField: { size: "small", fullWidth: true } }}
			/>
		</Box>

		{/* The size the change starts FROM, which is what the legacy filters on. There are 508
		    of them, so the combo has to be searchable — typing filters the list. Empty is
		    "All": clearing the field is what takes the filter off. */}
		<Box sx={{ width: 230 }}>
			<Autocomplete
				size="small"
				options={productSizes}
				value={productSizes.find((s) => s.Id === productSizeId) ?? null}
				getOptionLabel={(option) => option.Name}
				isOptionEqualToValue={(option, selected) => option.Id === selected.Id}
				onChange={(_event, option) => onProductSizeChange(option?.Id ?? null)}
				autoHighlight
				renderOption={(props, option) => {
					const { key, ...rest } = props as typeof props & { key: string };
					return (
						<li key={option.Id} {...rest}>
							{option.Name}
						</li>
					);
				}}
				renderInput={(params) => (
					<TextField {...params} label="Start Product Size" placeholder="All" />
				)}
			/>
		</Box>

		{/* A refresh, like the legacy's: it re-runs with whatever filters are on screen and
		    leaves them alone. It doubles as the way to run the report the first time, since
		    opening the page fetches nothing. */}
		<Tooltip title="Run the report with the filters above">
			<span>
				<IconButton
					onClick={onRefresh}
					disabled={isLoading}
					size="small"
					sx={{
						bgcolor: "primary.main",
						color: "white",
						width: 28,
						height: 28,
						"&:hover": { bgcolor: "primary.dark" },
						"&.Mui-disabled": {
							bgcolor: "action.disabledBackground",
							color: "action.disabled",
						},
					}}
				>
					<RefreshIcon sx={{ fontSize: 18 }} />
				</IconButton>
			</span>
		</Tooltip>

		<Box sx={{ flexGrow: 1, minWidth: 8 }} />

		<Box sx={{ width: 200 }}>
			<ExcelButton handleExportToExcel={onExportExcel} />
		</Box>
	</Box>
);
